<script lang="ts">
  import DesktopCredentialGate from "$lib/components/auth/DesktopCredentialGate.svelte";
  import "../app.css";
  import { onMount } from "svelte";
  import ConnectionStatus from "$lib/components/ConnectionStatus.svelte";
  import MessageSearch from "$lib/components/channel/MessageSearch.svelte";
  import { page } from "$app/state";
  import { goto } from "$app/navigation";
  import { oauthReturnTarget } from "$lib/oauth-login.js";
  import { workspaceState } from "$lib/state/app.svelte.js";
  import { isDesktopApp, isMac } from "$lib/utils.js";
  import Sonner from "$lib/components/ui/sonner/sonner.svelte";
  import { shellConfig } from "$lib/core/plugin.svelte.js";
  import { preferencesState } from "$lib/state/preferences.svelte.js";
  import { viewportState } from "$lib/state/viewport.svelte.js";
  import { isTauri, isTauriIOS, isTauriAndroid } from "$lib/mobile/push";
  import { installExternalLinkInterception } from "$lib/mobile/external-links.js";
  import { setOpen as setKeyboardOpen } from "$lib/mobile/keyboard-state";
  import { keyboardViewport, rebaselineHeight } from "$lib/mobile/keyboard-viewport";
  import { installSwipeBack } from "$lib/mobile/swipeBack";
  import { installGlobalErrorHandlers, reportClientError } from "@cyborg7/observability/web";
  import { appVersion } from "$lib/app-version.js";
  import { reportUiFailure } from "$lib/report-failure.js";
  import SecurityBannerSkipLink from "$lib/components/SecurityBannerSkipLink.svelte";
  import {
    reportPreviousRustPanic,
    scrubPanicText,
    type PreviousPanic,
  } from "$lib/mobile/rust-panic.js";

  // Start tracking the mobile breakpoint (no-op on SSR / desktop).
  viewportState.init();

  let hydrated = $state(false);
  onMount(() => {
    hydrated = true;
  });

  // Global error capture (window.onerror + unhandledrejection). Idempotent —
  // hooks.client.ts already installs these at module load; this is a
  // belt-and-suspenders re-arm on the first client mount.
  onMount(() => {
    installGlobalErrorHandlers({ version: appVersion() });
  });

  // Surface native Rust panics from the Tauri shell to the same observability
  // beacon JS errors use. The Rust panic hook (src-tauri/src/lib.rs) emits
  // `cyborg7://rust-panic` with { message, location }; forward it to
  // Was a nested ternary inline; same three outcomes, as a statement the linter
  // and a reader both take in one pass.
  function tauriPlatform(): string {
    if (isTauriIOS()) return "tauri-ios";
    if (isTauriAndroid()) return "tauri-android";
    return "tauri";
  }

  // reportClientError → relay /api/cyborg/client-log. No-op off the Tauri shell.
  onMount(() => {
    if (!isTauri()) return;
    const platform = tauriPlatform();
    let unlisten: (() => void) | undefined;
    let cancelled = false;
    void (async () => {
      try {
        const { listen } = await import("@tauri-apps/api/event");
        const stop = await listen<{ message?: string; location?: string }>(
          "cyborg7://rust-panic",
          (e) => {
            reportClientError({
              source: "tauri-rust",
              message: scrubPanicText(e.payload?.message ?? "Rust panic"),
              stack: e.payload?.location ? scrubPanicText(e.payload.location) : null,
              platform,
            });
          },
        );
        if (cancelled) stop();
        else unlisten = stop;
        // A panic that killed the previous launch never reached this listener: the
        // hook left it in rust_panic.txt, which this command reads and deletes.
        const { invoke } = await import("@tauri-apps/api/core");
        await reportPreviousRustPanic(platform, () =>
          invoke<PreviousPanic | null>("take_previous_rust_panic"),
        ).catch((err: unknown) => reportUiFailure("tauri.rust_panic.read", err));
      } catch (err) {
        console.warn("[panic] rust-panic listener failed", err);
      }
    })();
    return () => {
      cancelled = true;
      unlisten?.();
    };
  });

  // ── iOS keyboard / viewport foundation (Caveats #8, #14, #19, #20) ────────
  // Everything below is gated on `isTauriIOS()` so web / desktop / Android are
  // entirely unaffected — the listeners, CSS vars, and the `tauri-ios` class
  // only ever attach inside the Tauri iOS WKWebView shell.
  // Both native mobile shells: a WebView navigation replaces the app shell, so
  // external anchors must leave for the OS browser. Gated inside the module
  // (iOS OR Android), which is why this is NOT in the isTauriIOS block below.
  onMount(() => installExternalLinkInterception());

  // A desktop sign-in return that wakes the app off the sign-in pages (H10 D4): the shell holds it
  // until /login takes it.
  onMount(() => {
    const bridge = window.cyborg7Desktop?.oauth;
    if (!bridge) return;
    return bridge.onCallback(() => {
      const to = oauthReturnTarget(page.url.pathname);
      if (to) void goto(to);
    });
  });

  onMount(() => {
    if (!isTauriIOS()) return;

    const html = document.documentElement;
    // Scope hook for the iOS-only CSS in app.css (`html.tauri-ios …`).
    html.classList.add("tauri-ios");

    const cleanups: Array<() => void> = [];

    // ── visualViewport keyboard handling (Caveat #20) ──────────────────────
    const vv = window.visualViewport;
    if (vv) {
      const threshold = 150;
      let initialHeight = vv.height;
      let lastWidth = vv.width;

      // Pin --app-top at 0 unconditionally. With html + body both
      // `position: fixed; inset: 0` the layout viewport can't scroll, so
      // visualViewport.offsetTop SHOULD always be 0 — any non-zero value is a
      // transient WKWebView animation state. v1 learned that *following*
      // offsetTop caused the "top of app disappears for ~200ms" glitch, so we
      // never write --app-top from JS; the CSS default (0px) holds throughout.
      html.style.setProperty("--app-top", "0px");

      // Debounce the keyboard-OPEN→CLOSE transition. A mid-animation height
      // flick must not re-show the nav, so flip closed only once vv.height has
      // been stable for 200ms (past iOS's keyboard animation). No debounce on
      // SHOW so the nav can hide synchronously with the rising keyboard.
      let stabilizeTimer: ReturnType<typeof setTimeout> | null = null;
      const scheduleKeyboardState = (nextOpen: boolean) => {
        if (stabilizeTimer) {
          clearTimeout(stabilizeTimer);
          stabilizeTimer = null;
        }
        if (nextOpen) {
          setKeyboardOpen(true);
          return;
        }
        stabilizeTimer = setTimeout(() => {
          stabilizeTimer = null;
          setKeyboardOpen(false);
        }, 200);
      };

      const updateViewportVars = () => {
        html.style.setProperty("--app-vh", `${vv.height}px`);
      };

      const onResize = () => {
        // Rotation / new max height re-baselines the "no keyboard" height.
        if (vv.width !== lastWidth || vv.height > initialHeight) {
          initialHeight = vv.height;
          lastWidth = vv.width;
        }
        updateViewportVars();
        scheduleKeyboardState(initialHeight - vv.height > threshold);
        window.scrollTo(0, 0);
      };
      const onScroll = () => {
        updateViewportVars();
        window.scrollTo(0, 0);
      };

      onResize();
      vv.addEventListener("resize", onResize);
      vv.addEventListener("scroll", onScroll);
      cleanups.push(() => {
        vv.removeEventListener("resize", onResize);
        vv.removeEventListener("scroll", onScroll);
        if (stabilizeTimer) clearTimeout(stabilizeTimer);
      });

      // Native Swift `keyboardWillShow/Hide` call these BEFORE
      // visualViewport.resize fires, letting the nav hide synchronously with
      // the native composer's rise. They write through the same HMR-safe
      // keyboard-state module (Caveat #8) so a hot-reload can't orphan them.
      const win = window as unknown as {
        __cgKeyboardWillShow?: () => void;
        __cgKeyboardWillHide?: () => void;
      };
      win.__cgKeyboardWillShow = () => {
        if (stabilizeTimer) {
          clearTimeout(stabilizeTimer);
          stabilizeTimer = null;
        }
        setKeyboardOpen(true);
      };
      win.__cgKeyboardWillHide = () => {
        if (stabilizeTimer) {
          clearTimeout(stabilizeTimer);
          stabilizeTimer = null;
        }
        setKeyboardOpen(false);
      };
      cleanups.push(() => {
        delete win.__cgKeyboardWillShow;
        delete win.__cgKeyboardWillHide;
      });

      // Pre-empt iOS's focus-induced scroll. WKWebView's native "scroll
      // focused input into view" can fire before our visualViewport handler;
      // snap the document back to 0 synchronously + on the next frame.
      const onFocusIn = () => {
        window.scrollTo(0, 0);
        requestAnimationFrame(() => window.scrollTo(0, 0));
      };
      document.addEventListener("focusin", onFocusIn, true);
      cleanups.push(() =>
        document.removeEventListener("focusin", onFocusIn, true),
      );
    }

    // ── Belt-and-suspenders window scroll snap-back (Caveat #20, v1 parity) ──
    // WKWebView's inner UIScrollView can fire a `scroll` on window/document
    // AHEAD of visualViewport.scroll during the keyboard-show transition — the
    // brief "top of app disappears for ~200ms" glitch. The visualViewport
    // onScroll handler above only catches vv-level scrolls; this capture-phase
    // window listener catches window- AND document-level scrolls before any
    // other listener sees them, snapping both back to 0.
    const onWinScroll = () => {
      if (window.scrollY !== 0) window.scrollTo(0, 0);
      const se = document.scrollingElement as HTMLElement | null;
      if (se && se.scrollTop !== 0) se.scrollTop = 0;
    };
    window.addEventListener("scroll", onWinScroll, { passive: true, capture: true });
    cleanups.push(() =>
      window.removeEventListener("scroll", onWinScroll, { capture: true }),
    );

    // External-link interception moved to lib/mobile/external-links.ts and is
    // installed for BOTH mobile shells below — it was never iOS-only in cause,
    // only in the App Review consequence its old comment cited.

    // ── Swipe-back peek gesture (Caveats #21, #22, #23) ────────────────────
    // Edge-swipe from the left (25px band) translates the live <main> over a
    // dimmed clone of the previous page, committing past 80px / midpoint to the
    // explicitly-computed parent route (history.back() is unreliable). Installs
    // the history.pushState/replaceState capture wrap + the touch listeners +
    // renders the absolute peek layer imperatively; cleanup restores history
    // and removes the layer + listeners.
    cleanups.push(installSwipeBack());

    return () => {
      html.classList.remove("tauri-ios");
      for (const fn of cleanups) fn();
    };
  });

  shellConfig.configure({
    appName: "Cyborg",
    rail: { showLabels: true },
    features: { agents: true, tasks: true },
    // Grouped by concern: you (Account, Notifications, Appearance) → workspace
    // admin (Workspace, Members, Integrations, API & MCP, Billing) → infra/agents
    // (Backend, Daemon, AI, Providers) → About last. The `group` labels drive the desktop
    // dual-pane category headers (SettingsNav); mobile still renders one flat
    // scrollable tab row.
    //
    // The first tab keeps id "general" (SettingsNav + the mobile root list key
    // their icons off the id) but is LABELLED "Account" — it holds only
    // account-scoped things now; theme moved to its own Appearance tab and the
    // read-only workspace mirror was deleted in favour of the Workspace tab.
    // The personal cluster is grouped under "You": Profile (identity, ending in
    // account deletion — the root page) is the account half; Notifications and
    // Appearance are per-device prefs.
    settingsTabs: [
      { id: "general", label: "Account", href: "", group: "You" },
      { id: "appearance", label: "Appearance", href: "/appearance", group: "You", parentId: "general" },
      { id: "notifications", label: "Notifications", href: "/notifications", group: "You" },
      { id: "browser", label: "Browser", href: "/browser", group: "You" },
      { id: "workspace", label: "General", href: "/workspace", group: "Workspace" },
      { id: "members", label: "Members", href: "/members", group: "Workspace" },
      { id: "integrations", label: "Integrations", href: "/integrations", group: "Workspace" },
      // Slack and Microsoft Teams under it (settings-sections.ts).
      { id: "channel-sync", label: "Channel sync", href: "/slack-sync", group: "Workspace" },
      // Not a nav row (Channel sync lists it): names the page and keeps outsiders off it.
      { id: "teams-sync", label: "Microsoft Teams", href: "/teams-sync", group: "Workspace", parentId: "channel-sync" },
      { id: "developer", label: "API & MCP", href: "/developer", group: "Workspace" },
      { id: "webhooks", label: "Webhooks", href: "/webhooks", group: "Workspace" },
      { id: "billing", label: "Billing", href: "/billing", group: "Workspace" },
      { id: "daemon", label: "Machines", href: "/daemon", group: "Infrastructure" },
      { id: "providers", label: "Providers", href: "/providers", group: "Infrastructure", parentId: "daemon" },
      { id: "provider-usage", label: "Providers", href: "/provider-usage", group: "Infrastructure" },
      { id: "ai", label: "Workspace AI", href: "/ai", group: "Infrastructure" },
      { id: "logs", label: "Logs", href: "/logs", group: "Infrastructure" },
      { id: "about", label: "System", href: "/about", group: "Infrastructure" },
    ],
  });

  // Restore persisted preferences on the CLIENT after hydration. In dev (Vite
  // SSR) the preferencesState singleton is first built server-side, where its
  // constructor bails before reading localStorage, then reused on the client —
  // so without this re-read the persisted view preferences (Tasks ordering/
  // display, etc.) would not survive a refresh. The theme dodges this via the
  // inline boot script in app.html; the rest needs this explicit client re-read.
  onMount(() => {
    preferencesState.hydrateFromStorage();
  });

  // ── Android keyboard-follow (issue #460) ──────────────────────────────────
  // Parity with iOS WITHOUT a native composer: the soft keyboard shrinks the
  // VISUAL viewport (Chromium WebView default), so size the shell to
  // visualViewport.height (`--app-vh`) — the composer rides the keyboard
  // instead of being covered. Gated on isTauriAndroid() so web / desktop / iOS
  // are untouched; iOS keeps its own tuned block above.
  onMount(() => {
    if (!isTauriAndroid()) return;
    const html = document.documentElement;
    html.classList.add("tauri-android");
    const vv = window.visualViewport;
    if (!vv) return;

    let baseline = vv.height;
    let lastWidth = vv.width;

    const apply = () => {
      baseline = rebaselineHeight(
        { width: vv.width, height: vv.height },
        { width: lastWidth, baseline },
      );
      lastWidth = vv.width;
      const { appVh, keyboardInset, keyboardOpen } = keyboardViewport(vv.height, baseline);
      html.style.setProperty("--app-vh", `${appVh}px`);
      html.style.setProperty("--cg-keyboard-inset", `${keyboardInset}px`);
      setKeyboardOpen(keyboardOpen);
      // adjustPan WebViews can scroll the layout viewport up under the keyboard;
      // the shell is position:fixed, so snap the document back to 0 (parity with
      // the iOS scroll-snap) to keep the top bar pinned.
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };

    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
      html.classList.remove("tauri-android");
    };
  });

  // macOS Electron only: the native window floats traffic lights over the
  // titlebar (hiddenInset + trafficLightPosition x:12 in desktop-cyborg/main.ts,
  // darwin-only) that the search below must dodge. Windows/Linux get a
  // native frame with no floating lights, so --titlebar-traffic-light-inset
  // (app.css) must stay at its 0 default there — scope the 76px override to this
  // class (mirrors the html.tauri-ios pattern above) instead of leaving it
  // unconditional on :root, which reserved 76px of dead space on every
  // non-mac build (Windows ships this desktop shell too).
  onMount(() => {
    if (isDesktopApp() && isMac()) document.documentElement.classList.add("mac-titlebar");
  });

  let { children } = $props();

  // Slack-style global search: centered in the full-width titlebar on any
  // workspace route. Marked no-drag so the input is clickable inside the drag
  // region, while the empty sides stay grabbable for window dragging. Gated off
  // below the mobile breakpoint — the mobile shell renders its own search via
  // MobileTopBar/MobileSearchOverlay (workspace/[id]/+layout.svelte, gated on
  // the same viewportState.isMobile), so without this both searches existed at
  // once under ~640px.
  // The loaded workspace matches the route ⇒ the restore/switch splash is gone and
  // the shell chrome (titlebar, search) is meaningful. This is the exact condition
  // that clears `restoring` in workspace/[id]/+layout.svelte. NOTE: connection is
  // NOT the right signal — after login the socket connects (hasConnectedOnce flips
  // true) while the WORKSPACE is still loading; the search must wait for the
  // workspace, not just the socket. Stays true across brief reconnects (no flicker)
  // and goes false mid-switch (current is still the previous ws).
  const onWorkspaceRoute = $derived(page.url.pathname.startsWith("/workspace/"));
  const workspaceReady = $derived(workspaceState.current?.id === page.params.id);
  const showTitlebarSearch = $derived(
    onWorkspaceRoute && !viewportState.isMobile && workspaceReady,
  );
  // While a workspace route is still restoring/switching (its full-screen splash
  // is showing), blend the drag-region titlebar into that loading screen so no
  // stray grey strip (bg-canvas) sits above the spinner. Loaded state keeps the bar.
  const titlebarLoading = $derived(onWorkspaceRoute && !workspaceReady);

  // The search clears the macOS traffic lights on BOTH sides so it stays centered on the
  // window (the token is 0 off mac-desktop, app.css).
  const titlebarSearchInset = "var(--titlebar-traffic-light-inset)";

  // Where the connection banner (ConnectionStatus) docks relative to the search.
  // ABOVE the search (very top — the conventional "you're offline" bar) on every
  // surface EXCEPT the macOS desktop app: there the search strip IS the frameless
  // window titlebar (titleBarStyle:"hiddenInset" + floating traffic lights,
  // desktop-cyborg/main.ts is darwin-only), so a full-width banner above it would
  // fall under the window controls. On mac-desktop the banner therefore sits just
  // BELOW the titlebar instead. Windows/Linux use a native OS frame (the search is
  // plain body content) and web/iOS have no frameless titlebar, so they all get
  // the top placement. iOS is excluded from the mac case by isDesktopApp() even
  // though isMac() matches it — and top is exactly where the banner already lived
  // above the MobileTopBar there.
  const connBannerBelowTitlebar = $derived(isDesktopApp() && isMac());
</script>

<svelte:head>
  <title>Cyborg</title>
</svelte:head>

<!-- data-hydrated: inert e2e readiness marker (e2e/global-setup.ts); no UI/behaviour effect. -->
<div class="relative flex h-screen flex-col overflow-hidden" data-hydrated={hydrated || undefined}>
  <!-- The page's first Tab stop while a sign-in waits for an answer (B45). -->
  <SecurityBannerSkipLink />
  {#if !connBannerBelowTitlebar}
    <!-- Default placement: banner at the very top, ABOVE the search strip (the
         conventional offline-bar spot). Used on Windows/Linux desktop (native OS
         frame), all web, and iOS/Android (where it stacks above the MobileTopBar,
         its original home). See connBannerBelowTitlebar for the mac-desktop
         exception below. -->
    <ConnectionStatus />
  {/if}
  <div class="drag-region relative {titlebarLoading ? 'bg-surface' : 'bg-canvas'}">
    {#if showTitlebarSearch}
      <!-- Reserves titlebarSearchInset on BOTH left and right (F1), so this
           region is always centered on the WINDOW, not just on the space to
           one side. `w-full` fills that region; MessageSearch's
           own containerEl now owns the max-width + mx-auto clamp (see that
           component) — this div deliberately carries NO max-width of its own,
           so there is exactly one max-width in the chain and the two can't
           fight. Previously this div ALONE carried the clamp (max-w-[600px]);
           containerEl had none of its own (`class="relative"`) and simply
           inherited that 600px, while the visible PILL inside it carried an
           independent, narrower clamp (sm:w-[40vw] sm:min-w-[18rem]
           sm:max-w-[40rem]). The dropdown positions against containerEl, so
           it rendered at containerEl's (wider) box — up to ~±165px wider
           than the pill it was visually anchored under (640px viewport:
           containerEl 600px vs pill 270px; narrows to ±44px at 1280px:
           containerEl 600px vs pill 512px). Down to exactly one clamp, the
           two are pixel-exact (<0.02px) at every width.

           data-no-drag is NOT on this div — it's on MessageSearch's
           containerEl instead (see that component for why: this div is
           deliberately full-width with no max-width, and the no-drag hole
           matches whichever element carries the attribute). -->
      <div
        style="left: {titlebarSearchInset}; right: {titlebarSearchInset};"
        class="pointer-events-none absolute top-1/2 z-40 flex -translate-y-1/2 justify-center px-3"
      >
        <div class="pointer-events-auto w-full">
          <MessageSearch />
        </div>
      </div>
    {/if}
  </div>
  {#if connBannerBelowTitlebar}
    <!-- macOS desktop ONLY: the search strip above IS the frameless window
         titlebar (traffic lights), so the banner docks just BELOW it rather than
         at the very top where it would fall under the window controls. Either way
         it's a SIBLING of .drag-region, never a child — the titlebar search is
         absolutely positioned and window-centered INSIDE .drag-region, so a
         full-width banner in there would paint amber across the whole strip and
         collide with the centered search pill (whose 8%-alpha bg lets the amber
         bleed through). As its own row there's no overlap. -->
    <ConnectionStatus />
  {/if}
  <div class="flex-1 overflow-hidden">
    <DesktopCredentialGate>{@render children()}</DesktopCredentialGate>
  </div>
</div>
<Sonner />
